import {
  CSS_PROPERTY_DETAILS,
  PATH_TO_CSS_PROPERTY,
  Section,
  type WebflowCssStyleStorePath,
  type SectionType,
  type CustomProperties,
  type CssVariables,
  type CssPropertyDetail_Internal,
  type WebflowCssPropertyFull,
} from '@packages/systems/style/properties';
import type {WebflowCssProperty} from '@packages/systems/core/utils/WebflowCssSubset';
import {emptyArray} from '@packages/utilities/fp/utils';
import {entries} from '@packages/utilities/key-map';

type CssPropertyDetail = CssPropertyDetail_Internal<
  WebflowCssStyleStorePath | `--${string}`,
  WebflowCssPropertyFull
>;

export type {
  WebflowCssStyleStorePath,
  CssPropertyDetail,
  CustomProperties,
  CssVariables,
};
export {CSS_PROPERTY_DETAILS, PATH_TO_CSS_PROPERTY};

// Defines the order that CSS properties are rendered in when CSS is prettified
const CSS_PROPERTY_ORDER_: Array<{
  prop: WebflowCssProperty;
  group: SectionType;
  index?: number;
}> = [
  {prop: 'position', group: Section.LAYOUT},
  {prop: 'left', group: Section.LAYOUT},
  {prop: 'top', group: Section.LAYOUT},
  {prop: 'right', group: Section.LAYOUT},
  {prop: 'bottom', group: Section.LAYOUT},
  {prop: 'z-index', group: Section.LAYOUT},

  {prop: 'display', group: Section.BOX},
  {prop: 'visibility', group: Section.BOX},
  {prop: 'overflow', group: Section.BOX},
  {prop: 'overflow-x', group: Section.BOX},
  {prop: 'overflow-y', group: Section.BOX},
  {prop: 'box-sizing', group: Section.SIZING},
  {prop: 'width', group: Section.BOX},
  {prop: 'height', group: Section.BOX},
  {prop: 'max-height', group: Section.BOX},
  {prop: 'max-width', group: Section.BOX},
  {prop: 'min-height', group: Section.BOX},
  {prop: 'min-width', group: Section.BOX},
  {prop: 'margin', group: Section.BOX},
  {prop: 'margin-top', group: Section.BOX},
  {prop: 'margin-right', group: Section.BOX},
  {prop: 'margin-bottom', group: Section.BOX},
  {prop: 'margin-left', group: Section.BOX},
  {prop: 'padding', group: Section.BOX},
  {prop: 'padding-top', group: Section.BOX},
  {prop: 'padding-right', group: Section.BOX},
  {prop: 'padding-bottom', group: Section.BOX},
  {prop: 'padding-left', group: Section.BOX},
  {prop: 'float', group: Section.BOX},
  {prop: 'clear', group: Section.BOX},

  {prop: 'flex-direction', group: Section.BOX},
  {prop: 'justify-content', group: Section.BOX},
  {prop: 'justify-items', group: Section.BOX},
  {prop: 'flex-wrap', group: Section.BOX},
  {prop: 'align-items', group: Section.BOX},
  {prop: 'align-content', group: Section.BOX},
  {prop: 'align-self', group: Section.BOX},
  {prop: 'justify-self', group: Section.BOX},
  {prop: 'order', group: Section.BOX},
  {prop: 'flex-grow', group: Section.BOX},
  {prop: 'flex-shrink', group: Section.BOX},
  {prop: 'flex-basis', group: Section.BOX},
  {prop: 'flex-flow', group: Section.BOX},
  {prop: 'flex', group: Section.BOX},

  {prop: 'grid-auto-flow', group: Section.BOX},
  {prop: 'grid-auto-columns', group: Section.BOX},
  {prop: 'grid-auto-rows', group: Section.BOX},
  {prop: 'grid-column-gap', group: Section.BOX},
  {prop: 'grid-row-gap', group: Section.BOX},
  {prop: 'grid-template-areas', group: Section.BOX},
  {prop: 'grid-template-columns', group: Section.BOX},
  {prop: 'grid-template-rows', group: Section.BOX},
  {prop: 'aspect-ratio', group: Section.BOX},

  {prop: 'border', group: Section.BORDER},
  {prop: 'border-style', group: Section.BORDER},
  {prop: 'border-width', group: Section.BORDER},
  {prop: 'border-color', group: Section.BORDER},
  {prop: 'border-top', group: Section.BORDER},
  {prop: 'border-right', group: Section.BORDER},
  {prop: 'border-bottom', group: Section.BORDER},
  {prop: 'border-left', group: Section.BORDER},
  {prop: 'border-top-style', group: Section.BORDER},
  {prop: 'border-top-width', group: Section.BORDER},
  {prop: 'border-top-color', group: Section.BORDER},
  {prop: 'border-right-style', group: Section.BORDER},
  {prop: 'border-right-width', group: Section.BORDER},
  {prop: 'border-right-color', group: Section.BORDER},
  {prop: 'border-bottom-style', group: Section.BORDER},
  {prop: 'border-bottom-width', group: Section.BORDER},
  {prop: 'border-bottom-color', group: Section.BORDER},
  {prop: 'border-left-style', group: Section.BORDER},
  {prop: 'border-left-width', group: Section.BORDER},
  {prop: 'border-left-color', group: Section.BORDER},
  {prop: 'border-radius', group: Section.BORDER},
  {prop: 'border-top-left-radius', group: Section.BORDER},
  {prop: 'border-top-right-radius', group: Section.BORDER},
  {prop: 'border-bottom-left-radius', group: Section.BORDER},
  {prop: 'border-bottom-right-radius', group: Section.BORDER},

  {prop: 'background', group: Section.BACKGROUND},
  {prop: 'background-color', group: Section.BACKGROUND},
  {prop: 'background-image', group: Section.BACKGROUND},
  {prop: 'background-position', group: Section.BACKGROUND},
  {prop: 'background-position-x', group: Section.BACKGROUND},
  {prop: 'background-position-y', group: Section.BACKGROUND},
  {prop: 'background-size', group: Section.BACKGROUND},
  {prop: 'background-repeat', group: Section.BACKGROUND},
  {prop: 'background-repeat-x', group: Section.BACKGROUND},
  {prop: 'background-repeat-y', group: Section.BACKGROUND},
  {prop: 'background-attachment', group: Section.BACKGROUND},

  {prop: 'box-shadow', group: Section.EFFECTS},
  {prop: 'opacity', group: Section.EFFECTS},
  {prop: 'filter', group: Section.EFFECTS},
  {prop: 'backdrop-filter', group: Section.EFFECTS},
  {prop: 'outline-color', group: Section.EFFECTS},
  {prop: 'outline-offset', group: Section.EFFECTS},
  {prop: 'outline-style', group: Section.EFFECTS},
  {prop: 'outline-width', group: Section.EFFECTS},
  {prop: 'pointer-events', group: Section.EFFECTS},
  {prop: 'backface-visibility', group: Section.TRANSITIONS_AND_TRANSFORMS},
  {prop: 'perspective', group: Section.TRANSITIONS_AND_TRANSFORMS},
  {prop: 'perspective-origin', group: Section.TRANSITIONS_AND_TRANSFORMS},
  {prop: 'transform', group: Section.TRANSITIONS_AND_TRANSFORMS},
  {prop: 'transform-origin', group: Section.TRANSITIONS_AND_TRANSFORMS},
  {prop: 'transform-style', group: Section.TRANSITIONS_AND_TRANSFORMS},
  {prop: 'transition', group: Section.TRANSITIONS_AND_TRANSFORMS},
  {prop: 'transition-property', group: Section.TRANSITIONS_AND_TRANSFORMS},
  {prop: 'transition-duration', group: Section.TRANSITIONS_AND_TRANSFORMS},
  {
    prop: 'transition-timing-function',
    group: Section.TRANSITIONS_AND_TRANSFORMS,
  },
  {prop: 'transition-delay', group: Section.TRANSITIONS_AND_TRANSFORMS},

  {prop: 'direction', group: Section.TYPOGRAPHY},
  {prop: 'font-family', group: Section.TYPOGRAPHY},
  {prop: 'color', group: Section.TYPOGRAPHY},
  {prop: 'font-size', group: Section.TYPOGRAPHY},
  {prop: 'line-height', group: Section.TYPOGRAPHY},
  {prop: 'font-style', group: Section.TYPOGRAPHY},
  {prop: 'font-variant', group: Section.TYPOGRAPHY},
  {prop: 'font-variation-settings', group: Section.TYPOGRAPHY},
  {prop: 'font-weight', group: Section.TYPOGRAPHY},
  {prop: 'text-align', group: Section.TYPOGRAPHY},
  {prop: 'letter-spacing', group: Section.TYPOGRAPHY},
  {prop: 'text-decoration', group: Section.TYPOGRAPHY},
  {prop: '-webkit-text-stroke-width', group: Section.TYPOGRAPHY},
  {prop: '-webkit-text-stroke-color', group: Section.TYPOGRAPHY},
  {prop: 'text-indent', group: Section.TYPOGRAPHY},
  {prop: 'text-transform', group: Section.TYPOGRAPHY},
  {prop: 'list-style-type', group: Section.TYPOGRAPHY},
  {prop: 'text-shadow', group: Section.TYPOGRAPHY},
  {prop: 'text-overflow', group: Section.TYPOGRAPHY},
  {prop: 'font-smoothing', group: Section.TYPOGRAPHY},
  {prop: 'column-count', group: Section.TYPOGRAPHY},
  {prop: 'column-gap', group: Section.TYPOGRAPHY},
  {prop: 'column-rule', group: Section.TYPOGRAPHY},
  {prop: 'column-rule-color', group: Section.TYPOGRAPHY},
  {prop: 'column-rule-style', group: Section.TYPOGRAPHY},
  {prop: 'column-rule-width', group: Section.TYPOGRAPHY},
  {prop: 'column-span', group: Section.TYPOGRAPHY},
  {prop: 'white-space', group: Section.TYPOGRAPHY},
  {prop: 'word-break', group: Section.TYPOGRAPHY},
  {prop: 'overflow-wrap', group: Section.TYPOGRAPHY},
  {prop: 'text-underline-offset', group: Section.TYPOGRAPHY},
  {prop: 'text-decoration-color', group: Section.TYPOGRAPHY},
  {prop: 'text-decoration-style', group: Section.TYPOGRAPHY},
  {prop: 'text-decoration-thickness', group: Section.TYPOGRAPHY},
  {prop: 'text-decoration-skip-ink', group: Section.TYPOGRAPHY},
  {prop: 'text-underline-position', group: Section.TYPOGRAPHY},
  // this must come after typography because `text` clip overrides the color
  // property to be transparent
  {prop: 'background-clip', group: Section.TYPOGRAPHY},

  {prop: 'vertical-align', group: Section.LAYOUT},
  {prop: 'cursor', group: Section.OTHER},
  {prop: 'mix-blend-mode', group: Section.OTHER},
  {prop: 'object-fit', group: Section.OTHER},
  {prop: 'object-position', group: Section.OTHER},

  // Not implemented
  {prop: 'word-spacing', group: Section.TYPOGRAPHY},
  {prop: 'word-wrap', group: Section.TYPOGRAPHY},
];

const CSS_PROPERTY_ORDER_SET = new Set<string>(
  CSS_PROPERTY_ORDER_.map((item) => item.prop)
);

const CSS_PROPERTY_ORDER = CSS_PROPERTY_ORDER_.concat(
  entries(CSS_PROPERTY_DETAILS).flatMap(([prop, detail]) =>
    CSS_PROPERTY_ORDER_SET.has(prop)
      ? emptyArray
      : [{prop, group: detail.section}]
  )
);

type CssPropertyOrderMap = {
  [K in WebflowCssProperty]: {index: number; group: SectionType};
};
// Convert to a map
export const CSS_PROPERTY_ORDER_MAP: CssPropertyOrderMap =
  CSS_PROPERTY_ORDER.reduce((acc: CssPropertyOrderMap, item, index) => {
    item.index = index;
    acc[item.prop] = {index, group: item.group};
    return acc;
  }, {} as CssPropertyOrderMap);

/**
 * Sorts a list of styles by the logical order.
 * @param  {Object[]}   stylesArray   The list of style objects (these could be LESS, CSS, etc).
 * @param  {String}     namePath      The path to the name of the CSS property, relative to each object in the array.
 * @param  {Function}   hasRawValue   Optional predicate function used to determine if a property contains a Custom CSS value (@raw<|...|>)
 */
export function sortCssProperties<
  K extends string,
  T extends Partial<Record<K, string>>,
>(
  stylesArray: Array<T>,
  namePath: K,
  hasRawValue: null | ((prop: T, index: number) => boolean)
) {
  const MAX_ORDER = 9999;

  /**
   * A map of all the original indicies of the items in `stylesArray`, which is
   * used to provide an index value to `hasRawValue`.
   *
   * We do this for two reasons:
   *
   * 1. Below when we call `stylesArray.sort`, we are sorting the array _in
   *    place_, which means the index of each item in the array can change.
   * 2. Cloning the array would solve the above problem, but calling
   *    `stylesArray.indexOf` is O(n), and calling this many times in the body
   *    of a sort function means it could balloon into something like O(n^2).
   *    Pre-calculating ensures we only calculate these once, and map lookups
   *    are O(1).
   */
  const indexMap = new Map<T, number>();
  stylesArray.forEach((item, index) => {
    indexMap.set(item, index);
  });

  // Sort in place
  stylesArray.sort(function (lhs, rhs) {
    if (hasRawValue) {
      const lhsContainsCustomValue = hasRawValue(lhs, indexMap.get(lhs) ?? -1);
      const rhsContainsCustomValue = hasRawValue(rhs, indexMap.get(rhs) ?? -1);

      // Push A (custom value) to the bottom
      if (lhsContainsCustomValue && !rhsContainsCustomValue) {
        return 1;
      }
      // Push B (custom value) to the bottom
      if (!lhsContainsCustomValue && rhsContainsCustomValue) {
        return -1;
      }
      // Keep original order when both are custom values. This ensures we do
      // _not_ sort properties that are part of the Custom properties section of
      // the Style Panel
      if (lhsContainsCustomValue && rhsContainsCustomValue) {
        return 0;
      }
    }

    // Otherwise if neither contains custom values, sort based on our
    // `CSS_PROPERTY_ORDER_MAP`:
    // @ts-expect-error - TS2536 - Type 'T[K]' cannot be used to index type 'Dictionary<{ index: number; group: SectionType; }>'.
    const lhsProp = CSS_PROPERTY_ORDER_MAP[lhs[namePath]];
    const lhsIndex = lhsProp ? lhsProp.index : MAX_ORDER;

    // @ts-expect-error - TS2536 - Type 'T[K]' cannot be used to index type 'Dictionary<{ index: number; group: SectionType; }>'.
    const rhsProp = CSS_PROPERTY_ORDER_MAP[rhs[namePath]];
    const rhsIndex = rhsProp ? rhsProp.index : MAX_ORDER;

    return lhsIndex - rhsIndex;
  });
}
